import { isDefined } from './utilities';

/**
 * Adds another column to the "sort_answer" questions so users can have
 * both their answer and the correct answer.
 * 
 * To do so, it listens to all AJAX calls to check if it's the one 
 * getting the answers to the quiz. If it is, it gets all the 
 * "sort_answer" questions, it clones the items inside, and then 
 * orders the cloned items using the CSS property "order".
 */
export default class QuizComparisons {
	// eslint-disable-next-line require-jsdoc
	constructor(){
		// Intercept all AJAX call
		this.interceptAjaxCall();
	}

	/**
	 * Listens to all AJAX calls to check if it's the one getting 
	 * the answers to the quiz. If it is, it gets all the "sort_answer"
	 * questions and creates instances of QuizComparison, which handles
	 * the UI modifications
	 * 
	 * @return {undefined}
	 */
	interceptAjaxCall(){
		/**
		 * LearnDash is doing this call with jQuery, so we have to use it
		 * to intercept the call
		 */
		jQuery( document ).ajaxComplete(( event, jqXHR, ajaxOptions ) => {
			// Check if the user is trying to see the quiz results
			// action=ld_adv_quiz_pro_ajax&func=checkAnswers
			if ( ajaxOptions.data.includes( 'action=ld_adv_quiz_pro_ajax&func=checkAnswers' ) ){
				// Check if the response is defined
				if ( isDefined( jqXHR.responseJSON ) ){
					try {
						console.log( jqXHR.responseJSON );

						// Iterate questions and render the comparisons
						Object.keys( jqXHR.responseJSON ).forEach(( questionID ) => {
							// Get question data
							const question = jqXHR.responseJSON[ questionID ];

							// Check if it's a sorting question
							if ( isDefined( question.e ) && isDefined( question.e.type ) ){
								if ( question.e.type == 'sort_answer' ){
									// Create the comparison
									new QuizComparison({
										questionID:         questionID,
										answerCorrectOrder: question.e.c,
										answerUserOrder:    question.e.r
									});
								}
							}
						});
					} catch ( e ){
						console.log( e );
					}
				}
			}
		});
	}
}

/**
 * Helper class of the QuizComparisons class.
 * This one handles the UI modifications
 */
class QuizComparison {
	// eslint-disable-next-line require-jsdoc
	constructor({ questionID, answerCorrectOrder, answerUserOrder }){
		// Save data
		this.questionID         = questionID;
		this.answerCorrectOrder = answerCorrectOrder;
		this.answerUserOrder    = answerUserOrder;

		/**
		 * IMPORTANT
		 * The order of the invoked methods matters
		 */
		
		// Add unique class to the main wrapper
		this.addUniqueClassToWrapper();

		// Move default output inside container
		this.wrapAnswers();

		// Clone answers
		this.cloneAnswers();

		// Order the items
		this.orderItems();

		// Remove the colors from the correct-ordered items
		this.removeColorsCorrectItems();

		// Add the title of the sections "Correct answer", "Your answer"
		this.addSectionTitles();
	}

	/**
	 * Adds a CSS class to the main wrapper to prevent conflicts
	 * with other plugins
	 *
	 * @return {undefined}
	 */
	addUniqueClassToWrapper(){
		this.$questionList.classList.add( 'uo-evia-sorting-questions-comparison' );
	}

	/**
	 * Wraps all the items ordered in correct order.
	 * First it creates a wrapper, then it gets all the items, and
	 * appends them in the wrapper.
	 * 
	 * @return {undefined}
	 */
	wrapAnswers(){
		// Append a wrapper
		this.$questionList.insertAdjacentHTML( 'beforeend', `
			<li class="wpProQuiz_questionList_correct-order">
				<ul></ul>
			</li>
		` );

		// Get all items
		const $answerCorrectOrder = this.$questionList.querySelectorAll( 'li:not(.wpProQuiz_questionList_correct-order)' );

		// Move all items inside 
		[ ...$answerCorrectOrder ].forEach(( $item ) => {
			this.$answerCorrectOrderContainer.appendChild( $item );
		});
	}

	/**
	 * Clones the answers to sort them by the user order
	 * 
	 * @return {undefined}
	 */
	cloneAnswers(){
		// Append a wrapper
		this.$questionList.insertAdjacentHTML( 'beforeend', `
			<li class="wpProQuiz_questionList_user-order">
				<ul></ul>
			</li>
		` );

		// Iterate answers, clone them and append them to the new wrapper
		[ ...this.$itemsCorrectOrder ].forEach(( $item ) => {
			this.$answerUserOrderContainer.appendChild( $item.cloneNode( true ) );
		});
	}

	/**
	 * Orders the items so we show both the user order and the
	 * correct order
	 * 
	 * @return {undefined}
	 */
	orderItems(){
		// Order by user order
		this.answerUserOrder.forEach(( itemID, itemOrder ) => {
			// Get the node of the item
			const $item = this.$answerUserOrderContainer.querySelector( `li[data-pos="${ itemID }"]` );

			// Set the order
			$item.style.order = itemOrder;
		});
	}

	/**
	 * Removes the colors (red and green) from the correct-ordered items.
	 * We're doing this because we keep the colors only in the user-ordered
	 * items.
	 * 
	 * @return {undefined}
	 */
	removeColorsCorrectItems(){
		[ ...this.$itemsCorrectOrder ].forEach(( $item ) => {
			$item.classList.remove( 'wpProQuiz_answerIncorrect' );
			$item.classList.remove( 'wpProQuiz_answerCorrect' );
		});
	}

	/**
	 * Adds the title of the sections "Correct answer", "Your answer"
	 *
	 * @return {undefined}
	 */
	addSectionTitles(){
		// Add the title "Correct answer"
		this.$answerCorrectOrderContainer.insertAdjacentHTML( 'afterbegin', `
			<h4>${ Uncanny_Owl_Evia_Learning_SQC.i18n.correctAnswer }</h4>
		` );

		// Add the title "Your answer"
		this.$answerUserOrderContainer.insertAdjacentHTML( 'afterbegin', `
			<h4>${ Uncanny_Owl_Evia_Learning_SQC.i18n.yourAnswer }</h4>
		` );
	}

	/**
	 * Returns the node of the question list container
	 *
	 * @return {Node} The question list container
	 */
	get $questionList(){
		return document.querySelector( `.wpProQuiz_questionList[data-question_id="${ this.questionID }"]` );
	}

	/**
	 * Returns the node of the container with the items ordered in
	 * the correct order
	 *
	 * @return {Node} The container with the items ordered 
	 *                in the correct order
	 */
	get $answerCorrectOrderContainer(){
		return this.$questionList.querySelector( '.wpProQuiz_questionList_correct-order > ul' );
	}

	/**
	 * Returns the node of the container with the items ordered in
	 * the user order
	 *
	 * @return {Node} The container with the items ordered
	 *                in the user order
	 */
	get $answerUserOrderContainer(){
		return this.$questionList.querySelector( '.wpProQuiz_questionList_user-order > ul' );
	}

	/**
	 * Returns the node of all the items ordered in correct order
	 *
	 * @return {NodeList} The items
	 */
	get $itemsCorrectOrder(){
		return this.$answerCorrectOrderContainer.querySelectorAll( 'li' );
	}

	/**
	 * Returns the node of all the items ordered in user order
	 *
	 * @return {NodeList} The items
	 */
	get $itemsUserOrder(){
		return this.$answerUserOrderContainer.querySelectorAll( 'li' );
	}
}